/**
 * Chat Design System Tokens
 * Centralized constants for consistent styling across chat components
 */

// ── Shared Primitives (used to compose higher-level tokens) ────────
/** Core inline pill/badge layout — shared by PILL, BADGE, ACTION_BUTTON.pill, PROCESSING_INDICATOR */
const INLINE_PILL_BASE = 'inline-flex items-center rounded-sm border'

/** Compose an action button: base + variant + tactile + focusRing */
const actionBtn = (variant: string) =>
  `${ACTION_BUTTON.base} ${variant} ${INTERACTION.tactile} ${INTERACTION.focusRing}`

// Unified Visual Scale (Icons, Buttons, Spacing)
export const SCALE = {
  xs: 'w-3 h-3', // 12px - Tiny icons / dots
  sm: 'w-3.5 h-3.5', // 14px - Small icons
  md: 'w-4 h-4', // 16px - Standard icons / UI
  lg: 'w-5 h-5', // 20px - Large / Hero icons
  xl: 'w-9 h-9', // 36px - Canonical Action Button / Pill height
  '2xl': 'w-11 h-11', // 44px - Touch-safe minimum
} as const

// Border Radius — stark, flat, Vercel-like (no heavy pills/blobs)
export const RADIUS = {
  none: 'rounded-none',
  sm: 'rounded-sm', // 2px - Sharp cards
  md: 'rounded', // 4px - Medium cards/modals
  lg: 'rounded-md', // 6px - Large containers
  xl: 'rounded-lg', // 8px - Feature panels
  full: 'rounded-sm', // Formerly pills/avatars — now sharp
} as const

// Typography Scale (DM Sans — see DESIGN.md)
export const TEXT_SIZE = {
  tiny: 'text-xs', // 12px — DESIGN.md caption/label floor
  xs: 'text-xs', // 12px — mono labels, eyebrows, footer
  sm: 'text-xs', // 12px — nav / compact UI labels
  base: 'text-base', // 16px — primary body
  md: 'text-[17px]', // 17px — large body / emphasis
  lg: 'text-lg', // 18px — input areas / subheads
  xl: 'text-[18px]', // 18px - Subheaders
  '2xl': 'text-[24px]', // 24px - Section headers
  '3xl': 'text-[30px]',
  '4xl': 'text-[36px]',
  hero: 'text-[clamp(2.8rem,7.5vw,6rem)]', // display-hero, max 96px
} as const

// Spacing Rhythm
export const GAP = {
  xs: 'gap-1', // 4px
  sm: 'gap-2', // 8px
  md: 'gap-3', // 12px
  lg: 'gap-4', // 16px
  xl: 'gap-6', // 24px
  '2xl': 'gap-8', // 32px
} as const

// Padding Scale
export const PADDING = {
  xs: 'p-1.5',
  sm: 'p-2',
  md: 'p-3',
  lg: 'p-4',
  xl: 'p-6',
} as const

// Animation Durations
export const DURATION = {
  instant: 'duration-200', // 200ms - Instant feedback
  normal: 'duration-150', // 300ms - Standard transitions
  emphasis: 'duration-200', // 500ms - Emphasized animations
} as const

// Transition Types
export const TRANSITION = {
  all: 'transition-all',
  colors: 'transition-colors',
  opacity: 'transition-opacity',
  transform: 'transition-transform',
} as const

// Easing Curves
export const EASING = {
  spring: 'cubic-bezier(0.16, 1, 0.3, 1)', // Smooth spring animation
  inOut: 'ease-in-out',
  out: 'ease-out',
} as const

// Semantic Surface Roles - Flat, solid, Vercel-like
export const SURFACE_ROLES = {
  /** Root background - the deepest layer */
  base: 'bg-background text-foreground',
  /** Subsurface - used for sidebars or subtle grouping */
  muted: 'bg-muted text-muted-foreground border-border',
  /** Raised - cards, messages, interactive panels */
  raised: 'bg-card text-card-foreground border-border shadow-sm',
  /** Overlay - modals, dropdowns, floating menus */
  overlay: 'bg-popover text-popover-foreground border-border shadow-xl',
} as const

// Background Colors (Legacy/Utility) - Modern Minimal
export const BG = {
  default: SURFACE_ROLES.base,
  subtle: SURFACE_ROLES.muted,
  elevated: 'bg-secondary',
  card: SURFACE_ROLES.raised,
  glass: 'bg-background/95',
  hover: 'hover:bg-secondary',
  active: 'bg-accent',
  buttonSecondary: 'bg-secondary hover:bg-accent',
  avatar: 'bg-muted',
  /** Inverted surface — black on light, white on dark (+ matching text) */
  inverted: 'bg-foreground text-background',
} as const

// Border Colors - Modern Minimal
export const BORDER = {
  default: 'border-border',
  subtle: 'border-border/60',
  glass: 'border-border/60',
  /** Lighter glass border — cards, panels inside frosted containers */
  glassSubtle: 'border-border/40',
  /** Medium emphasis border — slightly lighter than default on dark */
  medium: 'border-border',
  hover: 'hover:border-ring',
  avatar: 'border-border',
} as const

// Text Colors - Modern Minimal
export const TEXT = {
  primary: 'text-foreground',
  secondary: 'text-muted-foreground',
  tertiary: 'text-muted-foreground',
  muted: 'text-muted-foreground',
  mutedHover: 'hover:text-foreground',
  /** Body text — slightly lighter than primary */
  body: 'text-foreground/80',
} as const

// Backdrop Blur — kept for legacy usage in Lightbox (full-screen overlay only)
export const BLUR = {
  sm: '',
  standard: '',
  strong: '',
} as const

// Shadows
export const SHADOW = {
  sm: 'shadow-sm shadow-black/20',
  md: 'shadow-md shadow-black/30',
  lg: 'shadow-lg shadow-black/40',
  xl: 'shadow-xl',
  '2xl': 'shadow-2xl',
  glow: 'shadow-lg shadow-amber-500/20',
} as const

/**
 * Glass System Guide:
 * - GLASS: Raw glass effects (subtle/medium/strong/card) — backdrop-blur levels
 * - SURFACE: Semantic surfaces that USE glass (glass, glassSubtle, glassStrong, section)
 * - CHAT_SURFACE: Chat-specific surfaces (shell, header, body, messageArea, etc.)
 *
 * Usage: Prefer SURFACE.glass over GLASS.medium for semantic clarity.
 * CHAT_SURFACE is only for chat page components.
 */

// Glass — replaced with flat solid surfaces (zero blur)
export const GLASS = {
  subtle: 'bg-muted border border-border',
  medium: 'bg-muted border border-border',
  strong: 'bg-card border border-border',
  card: 'bg-muted border border-border rounded-sm',
} as const

// State colors (for status indicators)
export const STATE_COLOR = {
  active: 'text-emerald-400',
  inactive: 'text-zinc-500',
  thinking: 'text-amber-500 dark:text-amber-400',
  researching: 'text-amber-500 dark:text-amber-400',
  tool: 'text-blue-500 dark:text-sky-400',
  error: 'text-red-500 dark:text-red-400',
  errorStrong: 'text-red-700 dark:text-red-300',
  success: 'text-emerald-500 dark:text-emerald-400',
} as const

// Shared Surfaces — flat, no glass, no blur
export const SURFACE = {
  glass: 'bg-muted border border-border',
  glassSubtle: 'bg-muted/60 border border-border',
  glassStrong: 'bg-card border border-border',
  glassHover: 'hover:bg-secondary',
  nav: 'bg-background border-b border-border',
  section: 'bg-background border border-border',
  input: 'bg-background border border-border',
} as const

// Shared Interaction Treatments
export const INTERACTION = {
  /** Standard focus ring — 2px accent ring, offset by background colour */
  focusRing:
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  /** Subtle lift — cards, feature tiles */
  premiumHover:
    'transition-transform duration-150 ease-out transform-gpu hover:-translate-y-0.5 hover:scale-[1.01] active:scale-[0.99] active:translate-y-0',
  /** Strong lift — primary CTAs, workshop cards */
  premiumHoverStrong:
    'transition-transform duration-150 ease-out transform-gpu hover:-translate-y-1 hover:scale-[1.02] active:scale-[0.98] active:translate-y-0',
  /** Tactile press — icon buttons */
  tactile:
    'transition-transform duration-150 ease-out transform-gpu hover:-translate-y-0.5 hover:scale-[1.03] active:scale-[0.98]',
  /** Shimmer sweep — place as first child inside `group relative overflow-hidden` */
  shimmer:
    'pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent group-hover:animate-shimmer',
  shimmerSoft:
    'pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/5 to-transparent group-hover:animate-shimmer',
  /** Nav link hover — no transform, pure colour transition */
  navHover: 'transition-colors duration-150 hover:text-foreground hover:bg-muted',
  /** Input field hover — border brightens before focus */
  inputHover: 'transition-colors duration-150 hover:border-foreground/30',
} as const

// Chat shell surfaces - Flat / Vercel-like minimal
export const CHAT_SURFACE = {
  app: 'bg-transparent text-foreground',
  /** Semi-transparent shell — lets AntigravityCanvas read through without softening text. */
  shell: 'bg-background/96 md:bg-background/92 text-foreground',
  handle: 'bg-muted-foreground/40',
  mobileHandle: 'bg-transparent',
  overlay: 'bg-background border border-border',
  dropdown: 'bg-popover text-popover-foreground border border-border shadow-md',
  /** AI messages — subtle frosted surface so text stays legible over Antigravity */
  messageAi:
    'rounded-sm border border-border/35 bg-background/72 px-4 py-3 text-foreground shadow-[0_12px_36px_rgba(0,0,0,0.28)] backdrop-blur-md supports-[backdrop-filter]:bg-background/60',
  /** User messages — subtle muted bubble */
  messageUser: 'bg-muted text-foreground rounded-sm px-5 py-3.5 max-w-[85%] self-end',
  attachment: 'bg-muted border border-border',
  stageBar: 'bg-background border-b border-border',
  /** Chat body — transparent (shell provides bg) */
  body: 'bg-transparent',
  /** Card interiors inside the chat */
  cardBody: 'bg-muted',
  /** Glass card — inline detail sub-cards */
  glassCard: 'rounded-sm border border-border bg-muted',
  /** Progress strip — sticky band between header and messages */
  progressStrip: 'bg-background/85 md:bg-background border-b border-border/40',
  /** Processing indicator shell */
  processingShell: 'bg-muted border border-border',
  /** Header surface — flat, opaque, minimal border only */
  header: 'bg-background border-b border-border/60',
} as const

export const CHAT_LAYOUT = {
  root:
    'fixed top-0 right-0 flex h-[100dvh] translate-x-0 translate-y-0 flex-col pointer-events-none z-[60] will-change-transform',
  panel:
    `relative flex h-full w-full flex-col overflow-hidden pointer-events-auto transition-colors duration-150 border-l border-border/40 ${CHAT_SURFACE.shell}`,
  header: `flex shrink-0 items-center justify-between px-4 py-3 z-10 ${CHAT_SURFACE.header}`,
  messageArea:
    'relative flex-1 overflow-y-auto px-3 py-4 text-foreground custom-scrollbar mask-image-gradient sm:px-6 sm:py-6',
  messageAreaCentered: 'flex flex-col justify-center',
  dropOverlay:
    `absolute inset-0 z-[100] m-2 flex flex-col items-center justify-center border-2 border-dashed border-border pointer-events-none ${CHAT_SURFACE.overlay}`,
  dropIcon: `mb-4 border ${BORDER.default} bg-background p-6 ${TEXT.body} shadow-sm`,
  resizeHandle:
    'hidden md:flex absolute top-0 left-0 -ml-1.5 h-full w-3 items-center justify-center cursor-ew-resize pointer-events-auto touch-none z-50 group/handle',
  resizeRule:
    'h-full w-px bg-transparent transition-colors duration-150 group-hover/handle:bg-muted-foreground/40 group-active/handle:bg-orange-500/50',
  mediaStack:
    'absolute top-20 right-4 z-50 flex flex-col items-end gap-3 pointer-events-none sm:right-6',
  mobileHandle:
    `flex w-full shrink-0 justify-center pb-2 pt-3 cursor-grab active:cursor-grabbing z-50 md:hidden ${CHAT_SURFACE.mobileHandle}`,
} as const

export const CHAT_INPUT = {
  root: 'relative z-40 w-full pointer-events-auto',
  actionSheet: 'px-4 pb-2 animate-float-up',
  actionGrid: `grid grid-cols-2 gap-2 border ${BORDER.default} bg-background/95 p-2 shadow-lg sm:grid-cols-3`,
  actionButton:
    'flex min-h-10 items-center gap-2 rounded-sm border border-border/60 bg-secondary/25 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-secondary/55 hover:text-foreground',
  actionButtonActive: 'border-[#c95f3a]/60 bg-[#c95f3a]/10 text-foreground',
  actionLabel: 'font-mono uppercase tracking-[0.14em]',
  uploadWrap: 'px-4 pb-2 animate-float-up',
  dock: `w-full border-t ${BORDER.default} bg-background/96 px-4 py-3 md:bg-background`,
  composer:
    `border ${BORDER.default} bg-background/72 transition-transform duration-150`,
  handleButton: 'flex w-20 items-center justify-center py-1',
  handleRule: 'h-0.5 w-10 bg-border/70',
  inputRow: 'flex items-end gap-2 px-3 pb-3',
  textarea:
    'flex-1 resize-none bg-transparent py-1 font-normal leading-relaxed text-foreground placeholder:text-muted-foreground/60 focus:outline-none custom-scrollbar',
  quickAction:
    'flex items-center gap-1.5 rounded-sm border border-border/60 bg-secondary/30 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:bg-secondary/55 hover:text-foreground',
  modeLabel:
    'ml-auto text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/70',
} as const

export const CHAT_MESSAGE = {
  row: 'group mb-6 flex w-full gap-3 sm:gap-4',
  stack: 'flex max-w-[85%] flex-col gap-2 md:max-w-[75%]',
  avatar:
    `flex ${SCALE.xl} shrink-0 items-center justify-center rounded-sm border transition-colors duration-150`,
  avatarUser: `${BG.inverted} border-[#c95f3a]`,
  avatarAi: 'bg-muted border-border text-foreground',
  liveTranscript:
    'bg-transparent border border-border/80 text-muted-foreground px-3 py-2 rounded-sm',
  detailsToggle:
    `flex items-center gap-1.5 rounded-sm border px-2.5 py-1 ${TEXT_SIZE.xs} font-mono transition-colors duration-150 ${INTERACTION.focusRing}`,
  actionBar:
    'flex items-center gap-1 self-end opacity-0 transition-opacity duration-150 group-hover:opacity-100',
  inlineAction:
    `flex items-center gap-1 rounded-sm border px-2 py-1 text-[10px] font-mono uppercase tracking-[0.16em] transition-colors ${CHAT_SURFACE.attachment} hover:bg-accent hover:text-foreground`,
  quote:
    'my-3 border-l-2 border-orange-500/60 bg-muted/55 px-3 py-2 text-muted-foreground',
} as const

export const CITATION = {
  button:
    `inline-flex h-5 min-w-5 items-center justify-center rounded-sm border px-1.5 ${CHAT_SURFACE.attachment} ${TEXT_SIZE.tiny} font-mono leading-none ${TEXT.secondary} transition-colors duration-150 ${INTERACTION.focusRing} hover:bg-accent hover:text-foreground whitespace-nowrap`,
  cluster: 'inline-flex items-center gap-0.5 mx-1 translate-y-[1px]',
} as const

// Unified Pill / Badge sizing (before ACTION_BUTTON so it can be referenced)
export const PILL = {
  /** Base pill — shared sizing for all status pills, capability hints, badges */
  base: `${INLINE_PILL_BASE} gap-1.5 px-2.5 py-1 text-xs font-medium`,
  /** Default surface — flat muted pill */
  surface: 'bg-muted border-border',
  /** Active surface — slightly stronger */
  activeSurface: 'bg-card border-border',
  /** Container — wraps multiple pills in a unified row */
  container: 'flex items-center gap-1.5 p-1 rounded-sm bg-muted border border-border',
} as const

// Unified inline and action buttons - Modern Minimal
export const ACTION_BUTTON = {
  base: 'group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-none border font-medium transition-all duration-150',
  primary: 'bg-foreground text-background border-border shadow-sm hover:bg-foreground/90',
  secondary: 'bg-card text-card-foreground border-border hover:bg-accent hover:border-ring',
  marketingPrimary: 'bg-foreground text-background border-transparent hover:bg-foreground/90',
  marketingSecondary:
    'bg-background text-foreground border-border hover:bg-muted transition-colors duration-150',
  outline:
    'bg-transparent text-muted-foreground border-border hover:bg-secondary hover:text-foreground',
  ghost:
    'bg-transparent text-muted-foreground border-transparent hover:bg-secondary hover:text-foreground',
  icon: `${SCALE.xl} rounded-sm border bg-card text-muted-foreground border-border hover:bg-accent hover:border-orange-500/40`,
  iconActive: `${SCALE.xl} rounded-sm border bg-foreground text-background border-border shadow-sm`,
  iconOutline: `${SCALE.xl} rounded-sm border bg-transparent text-muted-foreground border-border hover:bg-secondary hover:text-foreground`,
  menuItem:
    'group flex w-full items-start gap-3 px-4 py-3 text-left text-sm transition-all duration-200 hover:bg-secondary',
  /** Action pill — derives from PILL.base with mono uppercase treatment */
  pill: `${PILL.base} px-3 py-1.5 font-mono uppercase tracking-[0.2em]`,
  ring: "before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit] before:content-[''] before:ring-1 before:ring-border before:transition-opacity",
  activeHalo:
    "after:pointer-events-none after:absolute after:-inset-1 after:rounded-[inherit] after:content-[''] after:border after:border-orange-500/30 after:animate-pulse",
} as const

// Animation Classes
export const ANIMATION = {
  fadeIn: 'animate-fade-in',
  fadeInUp: 'animate-fade-in-up',
  fadeInScale: 'animate-fade-in-scale',
  slideUp: 'animate-slide-up',
  popIn: 'animate-pop-in',
  pulse: 'animate-pulse',
  ping: 'animate-ping',
  spin: 'animate-spin',
  bounce: 'animate-bounce',
} as const

/** Shared marketing copy — keep landing → chat CTAs aligned (DESIGN.md) */
export const COPY = {
  marketingCta: 'Try F.B/c Live',
  marketingCtaShort: 'Try F.B/c Live',
  scheduleCall: 'Schedule a call',
} as const

// Common Component Patterns
export const PATTERNS = {
  /** Standard icon button - using SCALE.xl (36px) */
  iconButton: `${SCALE.xl} flex items-center justify-center ${RADIUS.full} ${TRANSITION.colors} ${BG.hover}`,
  /** Primary action button */
  primaryButton: `${SCALE.xl} flex items-center justify-center ${RADIUS.full} ${TRANSITION.all} ${SHADOW.md}`,
  /** Status / Label Pill — derives from PILL.base with mono uppercase */
  pill: `${PILL.base} font-mono uppercase tracking-wide`,
  /** Standard Content Card */
  card: `${PADDING.md} ${RADIUS.md} border ${SURFACE_ROLES.raised}`,
  glassPanel: SURFACE.glass,
  /** IntelligenceBriefingCard root - full width, rounded, bordered, glass, shadow, fade-in */
  briefingCard: `w-full max-w-md ${RADIUS.lg} overflow-hidden border bg-background shadow-md ${ANIMATION.fadeInUp}`,
  actionButtonPrimary: actionBtn(ACTION_BUTTON.primary),
  actionButtonSecondary: actionBtn(ACTION_BUTTON.secondary),
  marketingPrimary: actionBtn(ACTION_BUTTON.marketingPrimary),
  /** Large marketing CTA — adds text-base px-8 py-4 for hero-level prominence */
  marketingPrimaryLarge: `${actionBtn(ACTION_BUTTON.marketingPrimary)} text-base px-8 py-4`,
  marketingSecondaryLarge: `${actionBtn(ACTION_BUTTON.marketingSecondary)} text-base px-8 py-4`,
  marketingSecondary: actionBtn(ACTION_BUTTON.marketingSecondary),
  actionButtonOutline: actionBtn(ACTION_BUTTON.outline),
  iconAction: `${ACTION_BUTTON.icon} ${INTERACTION.tactile} ${INTERACTION.focusRing}`,
} as const

// Semantic Colors — copper signal aligned with future-chat --fc-accent (#c95f3a)
export const BRAND = {
  accent: 'text-[#c95f3a]',
  accentHover: 'hover:text-[#c95f3a]',
  accentBg: 'bg-[#c95f3a]',
  accentBorder: 'border-[#c95f3a]/40',
  accentBgSubtle: 'bg-[#c95f3a]/10 dark:bg-[#c95f3a]/15',
  slash: 'text-[#c95f3a]',
  underline: 'decoration-[#c95f3a]/30',
} as const

export const SEMANTIC = {
  // Status colors — copper signal for primary accent states
  orange: 'bg-[#c95f3a]',
  blue: 'bg-blue-500 dark:bg-sky-400',
  purple: 'bg-purple-500 dark:bg-violet-400',
  green: 'bg-green-500',
  red: 'bg-red-500',

  orangeText: 'text-[#b8532f] dark:text-[#d9744f]',
  blueText: 'text-blue-600 dark:text-sky-400',
  purpleText: 'text-purple-600 dark:text-violet-400',
  greenText: 'text-green-500',
  redText: 'text-red-500',

  orangeBg: 'bg-[#c95f3a]/10 dark:bg-[#c95f3a]/15',
  blueBg: 'bg-blue-50 dark:bg-sky-900/20',
  purpleBg: 'bg-purple-50 dark:bg-violet-900/20',
  greenBg: 'bg-green-50 dark:bg-green-900/20',
  redBg: 'bg-red-50 dark:bg-red-900/20',
} as const

// Accessibility - Minimum touch targets
export const TOUCH_TARGET = {
  min: '44px', // iOS minimum (44x44pt)
  preferred: '48px', // Android preferred (48x48dp)
} as const

// Intelligence preflight console — always dark card so it reads on light + dark chat shells
export const PREFLIGHT_CONSOLE = {
  root:
    'relative w-full max-w-md select-none overflow-hidden rounded-sm border border-white/15 bg-black text-white/80 shadow-[0_18px_70px_rgba(0,0,0,0.34)]',
  body: 'relative z-0 px-4 py-5',
  scanline: 'pointer-events-none absolute inset-0 z-10 opacity-[0.07]',
  footer: 'mt-2 flex justify-between px-1 text-[10px] tabular-nums text-white/25',
} as const

// ProcessingIndicator (Thinking/Researching/Tool status)
// maxElapsedSeconds must match UI_TIMEOUT_DISPLAY_MS/1000 in src/core/contracts.ts
export const PROCESSING_INDICATOR = {
  elapsedVisibleAfterSeconds: 3,
  maxElapsedSeconds: 25, // Matches UI_TIMEOUT_DISPLAY_MS (25s) - actual agent timeout
  dotBg: 'bg-muted-foreground/50',
  iconBg: 'bg-secondary',
  iconBorder: 'border-border/50',
  queryPillBg: 'bg-blue-500/5 dark:bg-blue-500/10',
  queryPillBorder: 'border-blue-500/20',
  queryPillText: 'text-blue-600 dark:text-blue-400',
  statusTextExtra: 'tracking-tight',
  elapsedMargin: 'ml-1',
  shell: `${INLINE_PILL_BASE} max-w-full items-center gap-2.5 px-3 py-1.5 bg-background/85 border-border shadow-sm`,
  status: `${TEXT_SIZE.xs} font-medium tracking-tight text-foreground min-w-0 truncate`,
  detail: `${INLINE_PILL_BASE} px-2.5 py-1 text-[10px] font-mono text-muted-foreground border-border/60 bg-muted`,
  rail: 'flex items-center gap-1.5',
} as const

// Error panel (ErrorMessage)
export const ERROR_PANEL = {
  default: 'text-foreground bg-secondary border-border',
  muted: 'text-muted-foreground bg-secondary border-border',
} as const

// Fullscreen overlay (ScreenSharePreview, modals)
export const OVERLAY = {
  bg: 'bg-zinc-900',
  text: 'text-white',
  textMuted: 'text-white/70',
  textDim: 'text-white/60',
  textHint: 'text-white/50',
  backdrop: 'bg-black/80',
  cardBg: 'bg-black/80',
  buttonPrimary: 'bg-black text-white hover:bg-zinc-800',
  buttonSecondary: 'bg-zinc-700 text-white hover:bg-zinc-600',
  stopButton: 'bg-red-500/90 hover:bg-red-600',
} as const

// File upload states
export const FILE_UPLOAD = {
  dropZoneActive: 'border-ring bg-muted',
  dropZoneIdle: 'border-border hover:border-ring hover:bg-accent',
  iconActive: 'bg-secondary text-foreground',
  iconIdle: 'bg-secondary text-muted-foreground',
  fileRow: 'border-border bg-card',
  fileRowError: 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20',
  iconDefault: 'bg-secondary text-muted-foreground',
  iconError: 'bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400',
  progressTrack: 'bg-secondary',
  progressFill: 'bg-foreground',
} as const

// Code Block theme (syntax highlighting)
export const CODE_BLOCK = {
  background: 'bg-muted',
  text: 'text-foreground',
  token: 'text-foreground/80',
  comment: 'text-muted-foreground',
  lineNumber: 'text-muted-foreground/60',
  headerBg: 'bg-muted/50',
  headerBorder: 'border-border',
  headerText: 'text-muted-foreground',
  copyText: 'text-muted-foreground',
  copyHover: 'hover:text-foreground',
  copiedText: 'text-green-500',
  inlineBg: 'bg-secondary',
  inlineText: 'text-foreground',
} as const

// Z-Index Scale
export const Z_INDEX = {
  base: 'z-0',
  dropdown: 'z-10',
  sticky: 'z-20',
  overlay: 'z-40',
  modal: 'z-50',
  popover: 'z-60',
  toast: 'z-70',
  tooltip: 'z-[100]',
} as const

// ── Card Container Tokens ──────────────────────────────────────────
export const CARD = {
  /** Standard shadcn card — used in ui/card.tsx base */
  base: 'rounded-sm border border-border bg-background shadow-none',
  /** Card item row — for log entries, activity items in lists */
  itemRow:
    'flex min-w-0 max-w-full items-start gap-3 border-b border-border bg-transparent py-3 text-sm hover:bg-secondary/50 transition-colors',
  /** Metric card — bordered panel for stats / health metrics */
  metric:
    'flex min-w-0 max-w-full flex-col gap-1 break-words p-6 border-b md:border-b-0 md:border-r border-border bg-transparent',
  /** Admin empty/error state card */
  adminMuted: 'rounded-sm border border-border/60 bg-card/50 p-6 text-sm text-muted-foreground',
} as const

// ── Flex Layout Tokens ─────────────────────────────────────────────
export const FLEX = {
  /** flex items-center */
  row: 'flex items-center',
  /** flex items-center justify-center */
  center: 'flex items-center justify-center',
  /** flex items-center justify-between */
  between: 'flex items-center justify-between',
  /** flex flex-col */
  col: 'flex flex-col',
  /** flex items-center gap-1 */
  rowXs: 'flex items-center gap-1',
  /** flex items-center gap-2 */
  rowSm: 'flex items-center gap-2',
  /** flex items-center gap-3 */
  rowMd: 'flex items-center gap-3',
  /** flex items-center gap-4 */
  rowLg: 'flex items-center gap-4',
  /** flex flex-col gap-2 */
  colSm: 'flex flex-col gap-2',
  /** flex flex-col gap-3 */
  colMd: 'flex flex-col gap-3',
  /** flex flex-col gap-4 */
  colLg: 'flex flex-col gap-4',
} as const

// ── Hover State Tokens ─────────────────────────────────────────────
export const HOVER = {
  /** shadcn accent hover — sidebar items, ghost buttons */
  accent: 'hover:bg-accent hover:text-accent-foreground',
  /** Light glass hover — citations, inline buttons */
  glass: 'hover:bg-accent hover:text-foreground',
  /** Subtle glass hover — empty state items */
  glassSubtle: 'hover:bg-accent/60',
} as const

// ── Badge Base Tokens ──────────────────────────────────────────────
export const BADGE = {
  /** Shared base for all badge variants (shadcn + custom) */
  base: `${INLINE_PILL_BASE} px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2`,
  /** Compact badge with icon — lead status, tags */
  compact: `${INLINE_PILL_BASE} gap-1 px-2 py-0.5 text-xs font-medium`,
} as const

// ── Dark Mode Pattern Tokens ───────────────────────────────────────
/** @deprecated Use semantic tokens (bg-secondary, bg-muted, text-foreground, border-border) instead. Kept for backward compat during migration. */
export const DARK = {
  elevated: 'bg-secondary',
  overlay: 'bg-muted/60',
  textSecondary: 'text-muted-foreground',
  textMuted: 'text-muted-foreground/70',
  border: 'border-border',
  borderSubtle: 'border-border/20',
  hoverBg: 'hover:bg-accent',
  cardBg: 'bg-card/50',
  bgInactive: 'bg-muted-foreground/40',
  invertedText: 'text-primary-foreground',
  invertedBg: 'bg-primary',
  elevatedAlpha: 'bg-secondary/80',
  hoverBgSubtle: 'hover:bg-accent/30',
  borderMid: 'border-border',
  borderFaint: 'border-border/10',
  bgWhite10: 'bg-accent/20',
  textPrimary: 'text-foreground',
  textOrange: 'text-[#c95f3a] dark:text-[#d9744f]',
  borderWhite5: 'border-border/10',
} as const

// ── Status Color Tokens ──────────────────────────────────────
export const STATUS_COLORS = {
  /** Success state - green theme */
  success: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  /** Error state - red theme */
  error: 'bg-red-100 text-red-800 dark:bg-red-900/90 dark:text-red-300',
  /** Warning state - amber theme */
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
  /** Info state - blue theme */
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
} as const

// ── Border Color Tokens ───────────────────────────────────────
export const BORDER_COLORS = {
  /** Success borders */
  success: 'border-green-200 dark:border-green-800',
  /** Error borders */
  error: 'border-red-200 dark:border-red-800',
  /** Warning borders */
  warning: 'border-orange-200 dark:border-orange-800',
  /** Info borders */
  info: 'border-blue-200 dark:border-blue-800',
  /** Neutral borders */
  neutral: 'border-border',
} as const

// ── Backdrop Blur Tokens ───────────────────────────────────────
// BACKDROP — blur removed; kept as empty strings for zero-diff import compatibility
export const BACKDROP = {
  subtle: '',
  medium: '',
  strong: '',
  intense: '',
} as const

// ── Sidebar Navigation Tokens ──────────────────────────────────────
export const NAV = {
  /** Sidebar nav item — base layout, dense, snappy 150ms */
  item: 'flex items-center gap-2 rounded-sm px-3 py-1.5 text-xs transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
  /** Active indicator — left border, solid foreground (no orange hardcode) */
  activeIndicator: 'border-l-2 border-l-foreground',
} as const

// ══════════════════════════════════════════════════════════════════════
// UI COMPONENT TOKENS
// Single source of truth for all styling in src/components/ui/*
// ══════════════════════════════════════════════════════════════════════

// ── Button ─────────────────────────────────────────────────────────
export const BUTTON = {
  base: 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  variant: {
    default: 'bg-foreground text-background hover:bg-foreground/90',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    outline: `border border-input bg-background ${HOVER.accent}`,
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: HOVER.accent,
    link: 'text-primary underline-offset-4 hover:underline',
  },
  size: {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  },
} as const

/** Union of BUTTON variant keys */
export type ButtonVariant = keyof typeof BUTTON.variant
/** Union of BUTTON size keys */
export type ButtonSize = keyof typeof BUTTON.size

// ── Badge (shadcn variant tokens) ──────────────────────────────────
export const BADGE_VARIANT = {
  base: BADGE.base,
  variant: {
    default: 'border-transparent bg-foreground text-background hover:bg-foreground/80',
    secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
    destructive:
      'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
    outline: 'text-foreground',
    status: `px-3 py-1 border ${BORDER.default} ${BG.card} ${TEXT.primary} ${TEXT_SIZE.xs} font-medium tracking-wide`,
    metadata: `px-2.5 py-1 ${BG.elevated} ${TEXT_SIZE.xs} font-mono`,
    tool: `px-2 py-1.5 bg-secondary border ${BORDER.medium} ${TEXT_SIZE.xs} font-medium font-mono uppercase tracking-wider`,
    hint: `px-2.5 py-1 border ${BORDER.default} ${BG.subtle} ${TEXT.muted} ${TEXT_SIZE.tiny}`,
    research: `px-2.5 py-1 border ${BORDER.default} ${BG.subtle} ${TEXT.secondary} ${TEXT_SIZE.xs} font-mono`,
    metric: `px-1.5 py-0.5 border ${BORDER.default} ${BG.subtle} ${TEXT.tertiary} ${TEXT_SIZE.xs}`,
  },
} as const

/** Union of BADGE_VARIANT variant keys */
export type BadgeVariant = keyof typeof BADGE_VARIANT.variant

// ── Alert ──────────────────────────────────────────────────────────
export const ALERT = {
  base: 'relative w-full rounded-sm border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  variant: {
    default: 'bg-background text-foreground',
    destructive:
      'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
  },
  title: 'mb-1 font-medium leading-none tracking-tight',
  description: 'text-sm [&_p]:leading-relaxed',
} as const

/** Union of ALERT variant keys */
export type AlertVariant = keyof typeof ALERT.variant

// ── Label ──────────────────────────────────────────────────────────
export const LABEL = {
  base: 'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
} as const

// ── Progress ───────────────────────────────────────────────────────
export const PROGRESS = {
  root: 'relative h-4 w-full overflow-hidden rounded-full bg-secondary',
  indicator: 'h-full w-full flex-1 bg-primary transition-all',
} as const

// ── Separator ──────────────────────────────────────────────────────
export const SEPARATOR = {
  base: 'shrink-0 bg-border',
  horizontal: 'h-[1px] w-full',
  vertical: 'h-full w-[1px]',
} as const

// ── Switch ─────────────────────────────────────────────────────────
export const SWITCH = {
  root: 'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
  thumb:
    'pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
} as const

// ── Avatar ─────────────────────────────────────────────────────────
export const AVATAR = {
  root: 'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
  image: 'aspect-square h-full w-full',
  fallback: 'flex h-full w-full items-center justify-center rounded-full bg-muted',
} as const

// ── Dialog ─────────────────────────────────────────────────────────
export const DIALOG = {
  overlay:
    'fixed inset-0 z-[110] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  content:
    'fixed left-[50%] top-[50%] z-[110] grid w-[min(100%,calc(100vw-1.5rem))] max-w-lg min-w-0 translate-x-[-50%] translate-y-[-50%] gap-4 overflow-x-hidden break-words border bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:p-6 sm:rounded-lg',
  close:
    'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
  header: 'flex flex-col space-y-1.5 text-center sm:text-left',
  footer: 'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
  title: 'text-lg font-semibold leading-none tracking-tight',
  description: 'text-sm text-muted-foreground',
} as const

// ── Select ─────────────────────────────────────────────────────────
export const SELECT = {
  trigger:
    'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
  scrollButton: 'flex cursor-default items-center justify-center py-1',
  content:
    'relative z-[110] max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
  contentPopper:
    'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
  viewport: 'p-1',
  viewportPopper:
    'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
  label: 'py-1.5 pl-8 pr-2 text-sm font-semibold',
  item: 'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  separator: '-mx-1 my-1 h-px bg-muted',
} as const

// ── Tabs ───────────────────────────────────────────────────────────
export const TABS = {
  list: 'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
  trigger:
    'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
  content:
    'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
} as const

// ── Tooltip ────────────────────────────────────────────────────────
export const TOOLTIP = {
  content:
    'z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
} as const

// ── ScrollArea ─────────────────────────────────────────────────────
export const SCROLL_AREA = {
  root: 'relative min-w-0 max-w-full overflow-hidden',
  /** Radix sizes the viewport child as `display:table`, which grows to unbreakable URLs. Force a block that cannot widen the page. */
  viewport:
    'h-full w-full min-w-0 max-w-full rounded-[inherit] [&>div]:!block [&>div]:!w-full [&>div]:!min-w-0 [&>div]:!max-w-full',
  scrollbar: 'flex touch-none select-none transition-colors',
  scrollbarVertical: 'h-full w-2.5 border-l border-l-transparent p-[1px]',
  scrollbarHorizontal: 'h-2.5 flex-col border-t border-t-transparent p-[1px]',
  thumb: 'relative flex-1 rounded-full bg-border',
} as const

// ── Admin Surface Tokens ───────────────────────────────────────────
export const ADMIN_SURFACE = {
  /** Page wash — Industrial Editorial desk, not a white SaaS canvas */
  page: 'bg-[#FAFAFA] text-[#111315] dark:bg-background dark:text-foreground',
  sidebar: 'bg-background border-r border-border',
  sidebarActive: 'bg-secondary text-foreground border-l-2 border-l-[#c95f3a]',
  header: 'bg-background border-b border-border',
  contentArea: 'bg-transparent',
  /** Stat/metric card — copper rail, sharp corners, flat solid bg */
  statCard: 'rounded-none border border-border border-l-2 border-l-[#c95f3a] bg-background',
  /** Section panel — hairline, paper, no glass fill */
  panel: 'min-w-0 max-w-full rounded-sm border border-border bg-background',
  sectionHeader: `${TEXT_SIZE.xl} font-semibold ${TEXT.primary}`,
} as const

/** Dense admin card chrome — overrides shadcn p-6 / text-2xl */
export const ADMIN_CARD = {
  header: 'flex flex-col gap-1 space-y-0 p-3 sm:px-4 sm:py-3',
  title:
    'text-[13px] font-semibold uppercase tracking-[0.08em] leading-tight text-foreground [&_svg]:size-4',
  description: 'text-xs text-muted-foreground font-normal normal-case tracking-normal',
  content: 'p-3 pt-0 sm:p-4 sm:pt-0',
  footer: 'flex items-center p-3 pt-0 sm:p-4 sm:pt-0',
} as const

export const ADMIN_TABS = {
  list: 'flex h-auto w-full flex-wrap justify-start gap-0 rounded-none border-b border-border bg-transparent p-0',
  trigger:
    'min-h-11 rounded-none border-b-2 border-transparent px-3 py-2 text-xs font-medium uppercase tracking-[0.08em] shadow-none sm:min-h-9 data-[state=active]:border-[#c95f3a] data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none',
} as const

/** Admin chart frames — short on phones, copper/ink series. aspect-auto kills ChartContainer's aspect-video. */
export const ADMIN_CHART = {
  frame: 'aspect-auto h-44 w-full min-w-0 sm:h-60 lg:h-72',
  empty:
    'flex aspect-auto h-44 w-full min-w-0 items-center justify-center px-3 text-center text-xs text-muted-foreground sm:h-60 lg:h-72',
  stroke: '#c95f3a',
  strokeAlt: 'hsl(var(--foreground))',
  fill: 'rgba(201,95,58,0.22)',
  fillAlt: 'hsl(var(--foreground) / 0.12)',
  tooltip: {
    backgroundColor: 'hsl(var(--card))',
    border: '1px solid #c95f3a',
    borderRadius: '2px',
    color: 'hsl(var(--card-foreground))',
    fontSize: '12px',
  },
} as const

export const ADMIN_LAYOUT = {
  metricGrid: 'grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4',
  metricGridWide:
    'grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4 xl:grid-cols-5',
  metricGrid3: 'grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 md:grid-cols-3',
  sectionHeader: 'flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between',
  toolbar: 'flex min-w-0 w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end',
  chartGrid: 'grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2',
} as const

// ── Input & Textarea Tokens ────────────────────────────────────────
export const INPUT = {
  base: 'flex h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
} as const

export const TEXTAREA = {
  base: 'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
} as const

// ── Table Tokens ─────────────────────────────────────────────────
// High data-density: compact padding, snappy 150ms transitions, stark borders
export const TABLE_TOKENS = {
  wrapper: 'relative w-full overflow-auto',
  root: 'w-full caption-bottom text-xs',
  header: '[&_tr]:border-b',
  body: '[&_tr:last-child]:border-0',
  footer: 'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
  /** Snappy 150ms row hover — no blur, flat bg */
  row: 'border-b border-border/60 transition-colors duration-150 hover:bg-secondary/50 data-[state=selected]:bg-secondary focus-within:bg-secondary/30',
  /** Dense column headers */
  head: 'h-8 px-3 text-left align-middle font-semibold text-muted-foreground uppercase tracking-wide text-[10px] [&:has([role=checkbox])]:pr-0',
  /** Compact cells for high data-density */
  cell: 'px-3 py-2 align-middle text-xs [&:has([role=checkbox])]:pr-0',
  caption: 'mt-4 text-xs text-muted-foreground',
} as const

// ── Toast Tokens ───────────────────────────────────────────────────
// Square status line. Same mono / foreground / muted tokens as chat. No filled alert card.
export const TOAST = {
  shell: `pointer-events-auto fixed top-[calc(var(--fc-header-stack-height,3rem)+0.75rem)] left-1/2 z-[100] flex w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 items-start gap-3 border ${BORDER.default} ${BG.default} px-3 py-2 shadow-none transition-opacity duration-150 animate-fade-in-down sm:top-4 sm:w-auto ${RADIUS.none} ${TEXT_SIZE.xs} font-mono ${TEXT.primary}`,
  variant: {
    default: TEXT.primary,
    success: TEXT.primary,
    error: TEXT.primary,
    warning: TEXT.primary,
  },
  message: `min-w-0 font-mono ${TEXT_SIZE.xs} leading-snug ${TEXT.primary}`,
  close: `shrink-0 ${TEXT.muted} ${TEXT.mutedHover}`,
} as const

// ── Route Color Tokens ─────────────────────────────────────────────
export const ROUTE_COLOR = {
  active: 'bg-[#c95f3a]',
  ready: 'bg-muted-foreground/40',
  error: 'bg-red-500',
  voice: 'bg-[#c95f3a]',
} as const

// ── Font Weight Tokens ─────────────────────────────────────────────
export const WEIGHT = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
} as const

// ── Line Height (Leading) Tokens ───────────────────────────────────
export const LEADING = {
  tight: 'leading-tight',
  snug: 'leading-snug',
  normal: 'leading-normal',
  relaxed: 'leading-relaxed',
  loose: 'leading-loose',
} as const

// ── Grounding Source Card Tokens ────────────────────────────────────
export const SOURCE_CARD = {
  /** Source card shell — compact source proof below grounded answers */
  shell: `group/source flex min-w-[180px] items-center gap-2 rounded-sm border border-border bg-background/70 px-2.5 py-2 transition-colors duration-150 hover:bg-secondary hover:border-ring cursor-pointer`,
  /** Favicon image */
  favicon: 'w-4 h-4 rounded-sm flex-shrink-0',
  /** Favicon fallback — icon placeholder */
  faviconFallback: `w-4 h-4 rounded-sm flex-shrink-0 bg-secondary flex items-center justify-center ${TEXT_SIZE.tiny} ${TEXT.muted}`,
  /** Source title — truncated */
  title: `${TEXT_SIZE.xs} font-medium ${TEXT.primary} truncate`,
  /** Source domain — secondary text */
  domain: `${TEXT_SIZE.tiny} ${TEXT.muted} truncate`,
  /** Source strip container — horizontal scroll on mobile, vertical on desktop */
  strip: 'flex gap-2 overflow-x-auto pb-1 md:flex-col md:overflow-x-visible md:pb-0',
  /** Source strip wrapper — glass surface below message */
  stripWrapper: `mt-2 flex max-w-full gap-3`,
  /** Source sidebar — desktop right column */
  sidebar: `hidden lg:flex flex-col gap-2 ml-3 min-w-[180px] max-w-[220px] flex-shrink-0`,
  /** Hover card — glass pop-up for citation previews */
  hoverCard: `absolute bottom-full left-0 z-50 mb-2 min-w-[220px] max-w-[300px] rounded-sm border border-border bg-popover px-3 py-2.5 shadow-lg pointer-events-none`,
  hoverCardTitle: `${TEXT_SIZE.xs} font-medium ${TEXT.primary} truncate`,
  hoverCardDomain: `${TEXT_SIZE.tiny} ${TEXT.muted}`,
  hoverCardUrl: `${TEXT_SIZE.tiny} ${TEXT.secondary} truncate`,
} as const
